/* eslint-disable no-restricted-syntax */
import { AVAILABLE_TOKENS } from '../constants/tokenDefinitions';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

/**
 * Comprehensive validation for IVR prompt tokens
 * This function can be used on both client and server side
 */
export const validateIVRPrompt = (text: string): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic validation
  if (!text || text.trim().length === 0) {
    errors.push('Prompt cannot be empty');
    return { isValid: false, errors, warnings };
  }

  if (text.length > 1000) {
    warnings.push('Prompt is quite long. Consider keeping it concise for better user experience.');
  }

  // Find all angle bracket patterns
  const angleBracketPattern = /<[^>]+>/g;
  const matches = text.match(angleBracketPattern);
  
  if (!matches) {
    return { isValid: true, errors, warnings };
  }

  // Define the valid token patterns more explicitly
  const validTokenPatterns = [
    {
      pattern: /^<waitCount>$/,
      type: 'simple'
    },
    {
      pattern: /^<currentWaitTime\s*\|\s*\d+>$/,
      type: 'parameterized',
      baseName: 'currentWaitTime'
    },
    {
      pattern: /^<currentMaxWait\s*\|\s*\d+>$/,
      type: 'parameterized',
      baseName: 'currentMaxWait'
    },
    {
      pattern: /^<currentAvgWait\s*\|\s*\d+>$/,
      type: 'parameterized',
      baseName: 'currentAvgWait'
    }
  ];

  // Check each match
  matches.forEach((match, index) => {
    // Check if it's a valid token
    let isValidToken = false;
    let tokenInfo = null;
    
    for (const tokenDef of validTokenPatterns) {
      if (tokenDef.pattern.test(match)) {
        isValidToken = true;
        tokenInfo = tokenDef;
        break;
      }
    }
    
    if (!isValidToken) {
      errors.push(`Invalid token format: "${match}" at position ${index + 1}. Only use tokens from the available list.`);
      return;
    }
    
    // For parameterized tokens, validate the parameter value
    if (tokenInfo?.type === 'parameterized') {
      // Extract the parameter value
      const paramMatch = match.match(/<[^|]+\|\s*(\d+)\s*>/);
      if (paramMatch) {
        const parameter = parseInt(paramMatch[1], 10);
        
        if (parameter < 1) {
          errors.push(`Invalid token format: "${match}" - parameter must be at least 1`);
          return;
        }
        
        if (parameter > 60) {
          errors.push(`Invalid token format: "${match}" - parameter must be 60 or less`);
          return;
        }

        // Add warning for very high values
        if (parameter > 30) {
          warnings.push(`High wait time parameter (${parameter} minutes) in "${match}" - consider if this is realistic`);
        }
      }
    }
  });

  // Check for common mistakes in the original text
  const commonMistakeChecks = [
    {
      pattern: /<waitcount>/gi,
      message: 'Use <waitCount> (with capital C) instead of <waitcount>',
      exclude: /<waitCount>/g // Don't flag if the correct version is present
    },
    {
      pattern: /<currentwaittime(?:\s*\|[^>]*)?>/gi,
      message: 'Use <currentWaitTime> (with proper capitalization) instead of <currentwaittime>',
      exclude: /<currentWaitTime(?:\s*\|[^>]*)?>/g
    },
    {
      pattern: /<currentmaxwait(?:\s*\|[^>]*)?>/gi,
      message: 'Use <currentMaxWait> (with proper capitalization) instead of <currentmaxwait>',
      exclude: /<currentMaxWait(?:\s*\|[^>]*)?>/g
    },
    {
      pattern: /<currentavgwait(?:\s*\|[^>]*)?>/gi,
      message: 'Use <currentAvgWait> (with proper capitalization) instead of <currentavgwait>',
      exclude: /<currentAvgWait(?:\s*\|[^>]*)?>/g
    }
  ];

  commonMistakeChecks.forEach(({ pattern, message, exclude }) => {
    const mistakes = text.match(pattern);
    if (mistakes) {
      // Check if we should exclude this error (if correct version exists)
      const correctVersions = text.match(exclude);
      
      // Only flag as error if there are mistakes and they're not the correct versions
      const actualMistakes = mistakes.filter(mistake => {
        return !correctVersions?.some(correct => 
          correct.toLowerCase() === mistake.toLowerCase()
        );
      });
      
      if (actualMistakes.length > 0) {
        errors.push(message);
      }
    }
  });

  // Check for tokens with missing or invalid parameters
  const parameterTokens = text.match(/<(currentWaitTime|currentMaxWait|currentAvgWait)\s*\|[^>]*>/gi);
  if (parameterTokens) {
    parameterTokens.forEach(token => {
      // Check for empty parameter
      if (/\|\s*>/.test(token)) {
        errors.push(`Missing parameter after "|" in "${token}" - must include a number between 1-60`);
        return;
      }
      
      // Check for non-numeric parameter
      const paramMatch = token.match(/\|\s*([^>]+)/);
      if (paramMatch) {
        const param = paramMatch[1].trim();
        if (!/^\d+$/.test(param)) {
          errors.push(`Invalid parameter "${param}" in "${token}" - must be a number between 1-60`);
        }
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined
  };
};

/**
 * Server-side validation function that can be used in API endpoints
 * Returns a standardized error response
 */
export const validatePromptForServer = (text: string): { success: boolean; error?: string; warnings?: string[] } => {
  const validation = validateIVRPrompt(text);
  
  if (!validation.isValid) {
    return {
      success: false,
      error: `Prompt validation failed: ${validation.errors.join('; ')}`
    };
  }

  return {
    success: true,
    warnings: validation.warnings
  };
};